/**
 * Frontend mapping for the sanitised Stripe error codes the backend returns.
 *
 * The backend's `utils/stripe_errors.py::wrap_stripe_error` always responds
 * with `{code, message}` in the HTTPException detail. Use `stripeErrorToast`
 * below to pull the message out, so a response that somehow bypasses the
 * sanitiser still shows something sensible instead of "[object Object]".
 *
 * Keep this map in sync with `_CODE_MESSAGES` in the backend, we duplicate
 * the copy here so UX can evolve frontend text without a backend deploy.
 */
const STRIPE_CODE_COPY = {
  payout_account_issue:
    "There's an issue with your payout account. Please reconnect Stripe.",
  payout_setup_unfinished:
    "Your payout setup isn't finished. Please complete Stripe onboarding.",
  stripe_unavailable:
    "Payments are temporarily unavailable. Please try again in a moment.",
  stripe_config_error:
    "Payments are temporarily unavailable on our side. Our team has been notified.",
  card_declined: "Your card was declined. Please try a different payment method.",
  rate_limited: "We're busy right now, please retry in a few seconds.",
  stripe_error:
    "There was a problem with payments. Please try again.",
};

/**
 * Pull a user-facing message out of an Axios error response.
 *
 * Handles three shapes:
 *   1. Modern sanitised shape: detail = {code, message}
 *   2. Legacy string detail  : detail = "some text"
 *   3. Network error         : no response at all
 *
 * Always returns a non-empty string, never a raw Stripe message / account id.
 */
export function stripeErrorToast(err, fallback = 'Something went wrong, please try again.') {
  if (!err) return fallback;

  // Network error (no response body).
  if (!err.response) return 'Network issue, please check your connection.';

  const detail = err.response?.data?.detail;

  // Sanitised {code, message} shape from `wrap_stripe_error`.
  if (detail && typeof detail === 'object' && detail.code) {
    return STRIPE_CODE_COPY[detail.code] || detail.message || fallback;
  }

  // Legacy string detail, only returned by non-Stripe handlers (e.g.
  // "Booking not found"). Safe to show as-is because it doesn't come from
  // the Stripe SDK.
  if (typeof detail === 'string' && detail.length > 0) return detail;

  return fallback;
}

/**
 * True iff the error response is a sanitised Stripe error with the given
 * code. Use to branch UI, e.g. auto-refresh the Stripe card on
 * `payout_account_issue` so the CTA flips to "Reconnect".
 */
export function isStripeErrorCode(err, code) {
  const detail = err?.response?.data?.detail;
  return !!(detail && typeof detail === 'object' && detail.code === code);
}

export { STRIPE_CODE_COPY };
